import type {
    BridgeDelegate,
    BridgeGeoref,
    BridgeMarker,
    BridgePin,
    BridgeRoute,
    BridgeTrack,
} from '../../embed/MapBridge';
import type { IMapSurface } from './MapSurface';
import { metresToWorld } from './MapSurface';

export interface MapRouteMarkers {
    start?: BridgeMarker;
    ziel?: BridgeMarker;
    extra?: BridgeMarker[];
}

export interface MapExperienceAdapters {
    surface?: IMapSurface;
    onInit(payload: { lang?: string; theme?: string; georef?: BridgeGeoref }): void;
    pins: {
        setPins(pins: BridgePin[]): void | Promise<void>;
        focusPoi(id: number, zoom?: number): void;
        setActivePoi(id: number | null): void;
        setCategoryFilter(categories: string[] | null): void;
        refreshSurface?(): void;
    };
    routes: {
        setRoutes(routes: BridgeRoute[], markers?: MapRouteMarkers): void;
        selectRoute(routeId: number | string | null): void;
        setMarkersVisible?(visible: boolean): void;
        refreshSurface?(): void;
    };
    userPosition: {
        setPosition(position: { lat: number; lng: number; heading?: number; accuracy?: number } | null): void;
        refreshSurface?(): void;
    };
    camera: {
        flyTo(payload: { lat: number; lng: number; distance?: number; durationMs?: number }): void;
        zoomBy(factor: number): void;
    };
    overlays?: {
        setWaterwaysVisible(visible: boolean): void;
        refreshSurface?(): void;
    };
    tracks?: {
        setTracks(tracks: BridgeTrack[]): void;
    };
}

/**
 * Shared semantic state for every MapBridge renderer.
 *
 * It deliberately owns no GLB, tile or Three.js marker details. The adapters
 * are rendering strategies; this controller guarantees that filters never
 * mutate the authoritative pin set and that LOD height revisions refresh all
 * surface-bound visuals consistently.
 */
export class MapExperienceController implements BridgeDelegate {
    private readonly adapters: MapExperienceAdapters;
    private readonly unsubscribeHeightRevision: () => void;
    private pins: BridgePin[] = [];
    private routes: BridgeRoute[] = [];
    private routeMarkers?: MapRouteMarkers;
    private categoryFilter: string[] | null = null;
    private userPosition: { lat: number; lng: number; heading?: number; accuracy?: number } | null = null;
    private activePoiId: number | null = null;
    private pendingFocus: { id: number; distance?: number } | null = null;
    private pinsReady = false;
    private pinRevision = 0;

    constructor(adapters: MapExperienceAdapters) {
        this.adapters = adapters;
        this.unsubscribeHeightRevision = adapters.surface?.onHeightsChanged?.(() => this.refreshSurface())
            ?? (() => undefined);
    }

    onInit(payload: { lang?: string; theme?: string; georef?: BridgeGeoref }): void {
        this.adapters.onInit(payload);
    }

    onSetPins(pins: BridgePin[]): void {
        this.pins = pins.map(pin => ({ ...pin, model: pin.model ? { ...pin.model } : undefined }));
        this.pinsReady = false;
        const revision = ++this.pinRevision;
        const result = this.adapters.pins.setPins(this.pins);
        void Promise.resolve(result)
            .then(() => this.finishPinReplace(revision))
            .catch(error => {
                // A renderer may still have produced a partial pin set. Do not
                // leave selection/focus permanently queued after an async load
                // failure; the adapter remains responsible for visible errors.
                console.warn('[map] setPins renderer failed:', error);
                this.finishPinReplace(revision);
            });
    }

    onSetRoutes(routes: BridgeRoute[], markers?: MapRouteMarkers): void {
        this.routes = routes.map(route => ({
            ...route,
            points: route.points.map(point => ({ ...point })),
        }));
        this.routeMarkers = cloneRouteMarkers(markers);
        this.adapters.routes.setRoutes(this.routes, this.routeMarkers);
        this.adapters.routes.setMarkersVisible?.(this.categoryFilter === null);
    }

    onSelectRoute(routeId: number | string | null): void {
        this.adapters.routes.selectRoute(routeId);
    }

    onSetUserPosition(position: { lat: number; lng: number; heading?: number; accuracy?: number } | null): void {
        this.userPosition = position ? { ...position } : null;
        this.adapters.userPosition.setPosition(this.userPosition);
    }

    onFocusPoi(id: number, zoom?: number, distanceMeters?: number): void {
        const distance = this.focusDistanceWorld(zoom, distanceMeters);
        if (!this.pinsReady) {
            // Full-replace renderers may need glyphs/textures before a pin is
            // addressable. Last command wins, matching camera intent.
            this.pendingFocus = { id, distance };
            return;
        }
        this.adapters.pins.focusPoi(id, distance);
    }

    /** Kamera-Zielabstand fuer einen POI-Fokus, in WELT-Units.
     *
     *  Prioritaet: explizite Meter (v0.4 `distanceMeters`, surface-unabhaengig
     *  ueber worldUnitsPerMeter) > rohes v0.3 `zoom` (Engine-Units, Legacy) >
     *  ein NAHER Default. Der Default ist Vertragsbestandteil: "auf Karte
     *  anzeigen" MUSS die Kamera erkennbar hinfahren — vorher behielt der
     *  Terrain-Host die 17-km-Uebersichtsdistanz und der Fokus war unsichtbar
     *  (Alex 2026-08-16, Issue #1135). */
    private focusDistanceWorld(zoom?: number, distanceMeters?: number): number | undefined {
        const surface = this.adapters.surface;
        if (distanceMeters !== undefined && Number.isFinite(distanceMeters) && distanceMeters > 0 && surface) {
            return metresToWorld(surface, distanceMeters);
        }
        if (zoom !== undefined) return zoom;
        if (surface) return metresToWorld(surface, MapExperienceController.DEFAULT_FOCUS_DISTANCE_METERS);
        return undefined;
    }

    /** Nah-Fokus-Default: nah genug, dass Pin + Umfeld das Bild fuellen,
     *  weit genug fuer Orientierung — in beiden 3D-Welten identisch real. */
    static readonly DEFAULT_FOCUS_DISTANCE_METERS = 700;

    onSetActivePoi(id: number | null): void {
        this.activePoiId = id;
        if (this.pinsReady) this.adapters.pins.setActivePoi(id);
    }

    onFlyTo(payload: { lat: number; lng: number; distance?: number; distanceMeters?: number; durationMs?: number }): void {
        const { distanceMeters, ...rest } = payload;
        const surface = this.adapters.surface;
        const distance = distanceMeters !== undefined && Number.isFinite(distanceMeters) && distanceMeters > 0 && surface
            ? metresToWorld(surface, distanceMeters)
            : payload.distance;
        this.adapters.camera.flyTo({ ...rest, distance });
    }

    onZoomBy(factor: number): void {
        this.adapters.camera.zoomBy(factor);
    }

    onSetCategoryFilter(categories: string[] | null): void {
        this.categoryFilter = categories ? [...categories] : null;
        this.adapters.pins.setCategoryFilter(this.categoryFilter);
        // Contract v0.3 parity: any active category filter hides route markers.
        this.adapters.routes.setMarkersVisible?.(this.categoryFilter === null);
    }

    onSetWaterwaysVisible(visible: boolean): void {
        this.adapters.overlays?.setWaterwaysVisible(visible);
    }

    onSetTracks(tracks: BridgeTrack[]): void {
        this.adapters.tracks?.setTracks(tracks.map(track => ({
            ...track,
            silhouette: track.silhouette?.map(line => line.map(point => ({ ...point }))),
        })));
    }

    dispose(): void {
        this.pinRevision++;
        this.unsubscribeHeightRevision();
    }

    private finishPinReplace(revision: number): void {
        if (revision !== this.pinRevision) return;
        this.pinsReady = true;

        // Full-replace rendering recreates marker instances, therefore the
        // semantic selection must be re-applied even if the id did not change.
        this.adapters.pins.setActivePoi(this.activePoiId);

        const focus = this.pendingFocus;
        this.pendingFocus = null;
        if (focus) this.adapters.pins.focusPoi(focus.id, focus.distance);
    }

    private refreshSurface(): void {
        this.adapters.pins.refreshSurface?.();
        this.adapters.routes.refreshSurface?.();
        this.adapters.userPosition.refreshSurface?.();
        this.adapters.overlays?.refreshSurface?.();
    }
}

function cloneRouteMarkers(markers?: MapRouteMarkers): MapRouteMarkers | undefined {
    if (!markers) return undefined;
    return {
        start: markers.start ? { ...markers.start } : undefined,
        ziel: markers.ziel ? { ...markers.ziel } : undefined,
        extra: markers.extra?.map(marker => ({ ...marker })),
    };
}
